import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronUp, CircleAlert, Eye, EyeOff, ImageUp, Lock, Printer, RotateCcw, Trash2 } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import {
  companyCapabilities,
  emptyCompanyProfile,
  suggestedFooterColumns,
  type CompanyProfileGateway,
  type DocumentBlockPlacement,
  type TenantCompanyProfile,
} from '@forq/platform-data';
import { SearchSelect } from '@forq/ui';
import type { OrderDocumentKind } from '@forq/orders-data';
import { orderDocumentLabels, previewSampleOrder, renderOrderDocumentHtml, resolveOrderBlocks, sampleTemplateMachine, type OrderBlockState } from '@forq/orders/documents';
import { ReportDesignerSection } from './ReportDesigner';

interface CompanyFeatureProps {
  readonly tenant: TenantSummary;
  readonly gateway: CompanyProfileGateway;
  readonly canEdit: boolean;
}

/** A header image is inlined (data URI) so it prints without a network fetch. Raster images are
 *  downscaled to fit; this cap only guards SVGs and the (rare) still-too-large downscaled result. */
const maxLogoBytes = 800_000;

/**
 * Verarbeitet ein hochgeladenes Kopf-Bild zu einer data:-URI. Rasterbilder werden auf max. 640 px
 * Breite verkleinert (Canvas/PNG, Fallback JPEG), damit JEDES Bild in die Konfiguration passt --
 * das war die Ursache, dass große Logos vorher nicht gespeichert wurden. SVG bleibt Vektor.
 */
async function processLogoFile(file: File): Promise<{ dataUrl?: string; error?: string }> {
  if (file.type === 'image/svg+xml') {
    if (file.size > maxLogoBytes) return { error: `Das SVG ist ${Math.round(file.size / 1024)} KB groß – bitte unter ${maxLogoBytes / 1024} KB bleiben.` };
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ dataUrl: String(reader.result) });
      reader.onerror = () => resolve({ error: 'Das Bild konnte nicht gelesen werden.' });
      reader.readAsDataURL(file);
    });
  }
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      try {
        const scale = Math.min(1, 640 / (image.width || 640));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        if (!context) { resolve({ error: 'Das Bild konnte nicht verarbeitet werden.' }); return; }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        let dataUrl = canvas.toDataURL('image/png');
        if (dataUrl.length > maxLogoBytes) dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve({ dataUrl });
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    image.onerror = () => { resolve({ error: 'Das Bild konnte nicht gelesen werden.' }); URL.revokeObjectURL(objectUrl); };
    image.src = objectUrl;
  });
}

type Panel = 'appearance' | 'identity' | 'contact' | 'bank' | 'letterhead' | 'documents' | 'datev';

const panels: readonly { readonly id: Panel; readonly label: string }[] = [
  { id: 'appearance', label: 'Darstellung' },
  { id: 'identity', label: 'Firma' },
  { id: 'contact', label: 'Kontakt' },
  { id: 'bank', label: 'Bank' },
  { id: 'letterhead', label: 'Briefbogen' },
  { id: 'documents', label: 'Belege' },
  { id: 'datev', label: 'DATEV' },
];

/**
 * Die Grundkonfiguration eines Mandanten. Eine Fläche, die alles speist, was nach außen geht:
 * Briefbogen, E-Rechnung, DATEV-Mandat, Belegvorgaben. Die Fähigkeitsanzeige oben sagt dem
 * Anwender, was die nächste Eingabe FREISCHALTET — eine reine Pflichtfeld-Liste erklärt nie,
 * wofür man sie ausfüllt.
 */
export function CompanyFeature({ tenant, gateway, canEdit }: CompanyFeatureProps) {
  const [profile, setProfile] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [panel, setPanel] = useState<Panel>('identity');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    gateway.loadProfile(tenant.id)
      .then((loaded) => { if (active) setProfile(loaded); })
      .catch((loadError: unknown) => { if (active) setError(loadError instanceof Error ? loadError.message : 'Konfiguration konnte nicht geladen werden.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [gateway, tenant.id]);

  const capabilities = useMemo(() => companyCapabilities(profile), [profile]);

  const persist = useCallback(async (next: TenantCompanyProfile) => {
    setProfile(next);
    setSaving(true);
    setError(null);
    try {
      await gateway.saveProfile(tenant.id, next);
      // Die Oberfläche färbt sich live nach der Mandantenfarbe -- App.tsx hört auf dieses Event.
      window.dispatchEvent(new CustomEvent('forq:company-profile-saved', { detail: { tenantId: tenant.id } }));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1500);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [gateway, tenant.id]);

  const patch = useCallback(<K extends keyof TenantCompanyProfile>(key: K, value: Partial<TenantCompanyProfile[K]>) => {
    void persist({ ...profile, [key]: { ...profile[key], ...value } });
  }, [profile, persist]);

  const applySuggestedFooter = useCallback(() => {
    patch('letterhead', { footerColumns: suggestedFooterColumns(profile) });
  }, [patch, profile]);

  if (loading) return <p className="admin-empty">Grundkonfiguration wird geladen …</p>;

  return (
    <section className="admin-company">
      <header className="admin-company__head">
        <div>
          <h2>Grundkonfiguration · {tenant.name}</h2>
          <p>Die Firma hinter den Belegen. Diese Angaben tragen den Briefbogen, die E-Rechnung und den DATEV-Export – eine Quelle, damit Rechnung und Briefbogen nicht zwei verschiedene Firmen behaupten.</p>
        </div>
        {saving ? <span className="admin-company__state">Speichert …</span>
          : saved ? <span className="admin-company__state is-ok"><Check size={13} /> Gespeichert</span> : null}
      </header>

      {error && <div className="admin-company__error">{error}</div>}
      {!canEdit && <p className="admin-company__hint">Nur ansehen – zum Ändern fehlt die Berechtigung.</p>}

      <ul className="admin-company__capabilities">
        {capabilities.map((capability) => (
          <li key={capability.capability} className={capability.ready ? 'is-ready' : 'is-blocked'}>
            {capability.ready ? <Check size={14} aria-hidden /> : <CircleAlert size={14} aria-hidden />}
            <div>
              <strong>{capability.label}</strong>
              {capability.ready
                ? <small>einsatzbereit</small>
                : <small>{capability.issues.length} {capability.issues.length === 1 ? 'Angabe fehlt' : 'Angaben fehlen'}: {capability.issues[0]}</small>}
            </div>
          </li>
        ))}
      </ul>

      <nav className="admin-company__tabs" aria-label="Konfigurationsbereiche">
        {panels.map((entry) => (
          <button key={entry.id} type="button" className={entry.id === panel ? 'is-active' : ''} onClick={() => setPanel(entry.id)}>
            {entry.label}
          </button>
        ))}
      </nav>

      <div className="admin-company__body">
        {panel === 'appearance' && (
          <div className="admin-company__appearance">
            <label className="admin-company__color">Mandantenfarbe<input type="color" value={profile.letterhead.accentColor} disabled={!canEdit} onChange={(event) => patch('letterhead', { accentColor: event.target.value })} /></label>
            <p className="admin-company__hint">Diese Farbe kennzeichnet den Mandanten: Sie färbt die <strong>Oberfläche</strong> (Seitenleiste, Buttons, aktive Elemente) und die <strong>Belege</strong>. So erkennt man auf einen Blick, in welchem FORQ man gerade arbeitet. Am besten je Mandant eine deutlich andere Farbe wählen.</p>
            <div className="admin-appearance-preview">
              <span className="admin-appearance-preview__mark" style={{ background: profile.letterhead.accentColor }}>{tenant.shortName}</span>
              <button type="button" className="admin-appearance-preview__btn" style={{ background: profile.letterhead.accentColor }}>Beispiel-Aktion</button>
              <span className="admin-appearance-preview__chip" style={{ color: profile.letterhead.accentColor, borderColor: profile.letterhead.accentColor }}>Aktiver Eintrag</span>
            </div>
          </div>
        )}
        {panel === 'identity' && (
          <div className="admin-company__grid">
            <label>Firmenname<input value={profile.identity.name} disabled={!canEdit} onChange={(event) => patch('identity', { name: event.target.value })} /></label>
            <label>Rechtsform<input value={profile.identity.legalForm} disabled={!canEdit} placeholder="GmbH" onChange={(event) => patch('identity', { legalForm: event.target.value })} /></label>
            <label>Straße<input value={profile.identity.address.street} disabled={!canEdit} onChange={(event) => patch('identity', { address: { ...profile.identity.address, street: event.target.value } })} /></label>
            <label>PLZ<input value={profile.identity.address.postalCode} disabled={!canEdit} onChange={(event) => patch('identity', { address: { ...profile.identity.address, postalCode: event.target.value } })} /></label>
            <label>Ort<input value={profile.identity.address.city} disabled={!canEdit} onChange={(event) => patch('identity', { address: { ...profile.identity.address, city: event.target.value } })} /></label>
            <label>Land<input value={profile.identity.address.countryCode} disabled={!canEdit} maxLength={2} onChange={(event) => patch('identity', { address: { ...profile.identity.address, countryCode: event.target.value.toUpperCase() } })} /></label>
            <label>Handelsregister<input value={profile.identity.register} disabled={!canEdit} placeholder="HRB 1234" onChange={(event) => patch('identity', { register: event.target.value })} /></label>
            <label>Registergericht<input value={profile.identity.registerCourt} disabled={!canEdit} placeholder="Amtsgericht Arnsberg" onChange={(event) => patch('identity', { registerCourt: event.target.value })} /></label>
            <label>Geschäftsführung<input value={profile.identity.managingDirectors} disabled={!canEdit} onChange={(event) => patch('identity', { managingDirectors: event.target.value })} /></label>
            <label>USt-IdNr.<input value={profile.identity.vatId} disabled={!canEdit} placeholder="DE123456789" onChange={(event) => patch('identity', { vatId: event.target.value.trim() })} /></label>
            <label>Steuernummer<input value={profile.identity.taxNumber} disabled={!canEdit} placeholder="falls keine USt-IdNr." onChange={(event) => patch('identity', { taxNumber: event.target.value })} /></label>
          </div>
        )}

        {panel === 'contact' && (
          <div className="admin-company__grid">
            <label>Telefon<input value={profile.contact.phone} disabled={!canEdit} onChange={(event) => patch('contact', { phone: event.target.value })} /></label>
            <label>Fax<input value={profile.contact.fax} disabled={!canEdit} onChange={(event) => patch('contact', { fax: event.target.value })} /></label>
            <label>E-Mail<input type="email" value={profile.contact.email} disabled={!canEdit} onChange={(event) => patch('contact', { email: event.target.value.trim() })} /></label>
            <label>Website<input value={profile.contact.website} disabled={!canEdit} onChange={(event) => patch('contact', { website: event.target.value.trim() })} /></label>
            <label>Ansprechpartner<input value={profile.contact.contactName} disabled={!canEdit} onChange={(event) => patch('contact', { contactName: event.target.value })} /></label>
            <label>Telefon Ansprechpartner<input value={profile.contact.contactPhone} disabled={!canEdit} onChange={(event) => patch('contact', { contactPhone: event.target.value })} /></label>
            <label>E-Mail Ansprechpartner<input type="email" value={profile.contact.contactEmail} disabled={!canEdit} onChange={(event) => patch('contact', { contactEmail: event.target.value.trim() })} /></label>
            <p className="admin-company__note is-wide">Der Ansprechpartner ist in der XRechnung Pflicht (BG-6) – ohne ihn verweigert FORQ die E-Rechnung.</p>
          </div>
        )}

        {panel === 'bank' && (
          <div className="admin-company__grid">
            <label>Bank<input value={profile.bank.bankName} disabled={!canEdit} onChange={(event) => patch('bank', { bankName: event.target.value })} /></label>
            <label>IBAN<input value={profile.bank.iban} disabled={!canEdit} placeholder="DE.." onChange={(event) => patch('bank', { iban: event.target.value })} /></label>
            <label>BIC<input value={profile.bank.bic} disabled={!canEdit} onChange={(event) => patch('bank', { bic: event.target.value.trim().toUpperCase() })} /></label>
            <p className="admin-company__note is-wide">Die IBAN wird auf ihre Prüfziffer geprüft – ein Zahlendreher fällt hier auf und nicht erst beim Kunden.</p>
          </div>
        )}

        {panel === 'letterhead' && (
          <div className="admin-company__letterhead">
            <div className="admin-company__grid">
              <div className="admin-company__logos is-wide">
                <span className="admin-company__logos-title">Briefkopf – drei Bilder, links · Mitte · rechts (z. B. Logo links, Leistungsgrafik rechts)</span>
                <div className="admin-company__logos-slots">
                  <LogoSlot label="Kopf links" value={profile.letterhead.logoDataUrl} canEdit={canEdit} onChange={(value) => patch('letterhead', { logoDataUrl: value })} onError={setError} />
                  <LogoSlot label="Kopf Mitte" value={profile.letterhead.logoCenterDataUrl} canEdit={canEdit} onChange={(value) => patch('letterhead', { logoCenterDataUrl: value })} onError={setError} />
                  <LogoSlot label="Kopf rechts" value={profile.letterhead.logoRightDataUrl} canEdit={canEdit} onChange={(value) => patch('letterhead', { logoRightDataUrl: value })} onError={setError} />
                </div>
              </div>
              <label>Akzentfarbe<input type="color" value={profile.letterhead.accentColor} disabled={!canEdit} onChange={(event) => patch('letterhead', { accentColor: event.target.value })} /></label>
              <label>Falzmarken
                <SearchSelect
                  value={profile.letterhead.showFoldMarks ? 'yes' : 'no'}
                  disabled={!canEdit}
                  onChange={(value) => patch('letterhead', { showFoldMarks: value === 'yes' })}
                  options={[{ value: 'yes', label: 'Andrucken (DIN 5008)' }, { value: 'no', label: 'Nicht andrucken' }]}
                />
              </label>
              <label className="is-wide">Absenderzeile über dem Anschriftfeld
                <input value={profile.letterhead.senderLine} disabled={!canEdit} placeholder="Fördertechnik Werl · Industriestraße 1 · 59457 Werl" onChange={(event) => patch('letterhead', { senderLine: event.target.value })} />
              </label>
              {profile.letterhead.footerColumns.map((column, index) => (
                <label key={index}>Fußzeile Spalte {index + 1}
                  <textarea rows={4} value={column} disabled={!canEdit} onChange={(event) => {
                    const next = [...profile.letterhead.footerColumns];
                    next[index] = event.target.value;
                    patch('letterhead', { footerColumns: next });
                  }} />
                </label>
              ))}
              <div className="is-wide">
                <button type="button" className="admin-company__ghost" disabled={!canEdit} onClick={applySuggestedFooter}>
                  <RotateCcw size={13} /> Fußzeile aus den Firmendaten vorschlagen
                </button>
              </div>
            </div>

            <LetterheadPreview profile={profile} tenantName={tenant.name} />
          </div>
        )}

        {panel === 'documents' && (
          <div className="admin-company__grid">
            <label>Zahlungsziel (Tage)<input type="number" min={0} value={profile.documentDefaults.paymentTermDays} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { paymentTermDays: Number(event.target.value) })} /></label>
            <label>Skonto (%)<input type="number" min={0} max={100} step="0.1" value={profile.documentDefaults.cashDiscountPercent} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { cashDiscountPercent: Number(event.target.value) })} /></label>
            <label>Skontofrist (Tage)<input type="number" min={0} value={profile.documentDefaults.cashDiscountDays} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { cashDiscountDays: Number(event.target.value) })} /></label>
            <label>Angebot gültig (Tage)<input type="number" min={0} value={profile.documentDefaults.quoteValidityDays} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { quoteValidityDays: Number(event.target.value) })} /></label>
            <label className="is-wide">Schlusstext Angebot<textarea rows={3} value={profile.documentDefaults.quoteFooterText} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { quoteFooterText: event.target.value })} /></label>
            <label className="is-wide">Schlusstext Rechnung<textarea rows={3} value={profile.documentDefaults.invoiceFooterText} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { invoiceFooterText: event.target.value })} /></label>
            <label className="is-wide">Schlusstext Lieferschein<textarea rows={3} value={profile.documentDefaults.deliveryNoteFooterText} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { deliveryNoteFooterText: event.target.value })} /></label>
            <p className="admin-company__note is-wide">Anrede/Einleitung je Belegart. Platzhalter <code>{'{titel}'}</code>, <code>{'{kunde}'}</code>, <code>{'{nummer}'}</code> werden beim Drucken durch die Vorgangsdaten ersetzt. Leer lassen = eingebauter Standardtext.</p>
            <label className="is-wide">Anrede Angebot<textarea rows={2} value={profile.documentDefaults.offerIntro} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { offerIntro: event.target.value })} /></label>
            <label className="is-wide">Anrede Auftragsbestätigung<textarea rows={2} value={profile.documentDefaults.confirmationIntro} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { confirmationIntro: event.target.value })} /></label>
            <label className="is-wide">Anrede Lieferschein<textarea rows={2} value={profile.documentDefaults.deliveryNoteIntro} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { deliveryNoteIntro: event.target.value })} /></label>
            <label className="is-wide">Anrede Werkstattauftrag<textarea rows={2} value={profile.documentDefaults.workshopOrderIntro} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { workshopOrderIntro: event.target.value })} /></label>
            <p className="admin-company__note is-wide">Rechnung: Einleitung + Zahlungssatz. Platzhalter <code>{'{kunde}'}</code>, <code>{'{nummer}'}</code>, <code>{'{fällig}'}</code> (Fälligkeitsdatum). Der Skonto-Satz wird bei hinterlegtem Skonto automatisch ergänzt.</p>
            <label className="is-wide">Anrede Rechnung<textarea rows={2} value={profile.documentDefaults.invoiceIntro} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { invoiceIntro: event.target.value })} /></label>
            <label className="is-wide">Zahlungstext Rechnung<textarea rows={2} value={profile.documentDefaults.invoicePaymentText} disabled={!canEdit} onChange={(event) => patch('documentDefaults', { invoicePaymentText: event.target.value })} /></label>
            <DocumentLayoutSection
              profile={profile}
              canEdit={canEdit}
              onLayoutChange={(blockLayouts) => patch('documentDefaults', { blockLayouts })}
            />
            <ReportDesignerSection
              profile={profile}
              canEdit={canEdit}
              onPatch={(partial) => patch('documentDefaults', partial)}
            />
          </div>
        )}

        {panel === 'datev' && (
          <div className="admin-company__grid">
            <label>Beraternummer<input value={profile.datev.consultantNumber} disabled={!canEdit} inputMode="numeric" placeholder="1001" onChange={(event) => patch('datev', { consultantNumber: event.target.value.trim() })} /></label>
            <label>Mandantennummer<input value={profile.datev.clientNumber} disabled={!canEdit} inputMode="numeric" placeholder="55" onChange={(event) => patch('datev', { clientNumber: event.target.value.trim() })} /></label>
            <label>Beginn Wirtschaftsjahr<input type="date" value={profile.datev.fiscalYearStart} disabled={!canEdit} onChange={(event) => patch('datev', { fiscalYearStart: event.target.value })} /></label>
            <label>Länge der Sachkonten<input type="number" min={4} max={8} value={profile.datev.accountLength} disabled={!canEdit} onChange={(event) => patch('datev', { accountLength: Number(event.target.value) })} /></label>
            <p className="admin-company__note is-wide">Berater- und Mandantennummer kommen vom Steuerberater und stehen im Kopf jeder EXTF-Datei. Ohne sie weist DATEV den Import ab – geraten wird hier nichts.</p>
          </div>
        )}
      </div>
    </section>
  );
}

/** Ein Bild-Slot des Briefkopfs (links/Mitte/rechts) mit eigenem Upload + Entfernen. */
function LogoSlot({ label, value, canEdit, onChange, onError }: {
  readonly label: string; readonly value: string; readonly canEdit: boolean;
  readonly onChange: (dataUrl: string) => void; readonly onError: (message: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const pick = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // erlaubt, dieselbe Datei erneut zu wählen
    if (!file) return;
    onError('');
    void processLogoFile(file).then((result) => {
      if (result.error) onError(result.error);
      else if (result.dataUrl) onChange(result.dataUrl);
    });
  }, [onChange, onError]);
  return (
    <div className="admin-company__logo">
      <span>{label}</span>
      <div>
        {value ? <img src={value} alt={label} /> : <span className="admin-company__logo--empty">Kein Bild</span>}
        <div className="admin-company__logo-actions">
          <input ref={input} type="file" accept="image/png,image/jpeg,image/svg+xml" hidden onChange={pick} />
          <button type="button" disabled={!canEdit} onClick={() => input.current?.click()}><ImageUp size={13} /> Hochladen</button>
          {value && <button type="button" disabled={!canEdit} onClick={() => onChange('')}><Trash2 size={13} /> Entfernen</button>}
        </div>
      </div>
    </div>
  );
}

/**
 * Der Briefbogen, wie er auf Papier landet. Bewusst in mm gerechnet und nach DIN 5008 aufgebaut
 * (Anschriftfeld links oben, Falz- und Lochmarken am linken Rand) — dieselbe Definition trägt
 * später die PDF-Ausgabe, deshalb wird hier nicht „ungefähr wie ein Brief" gezeichnet.
 */
const previewKinds: readonly OrderDocumentKind[] = ['offer', 'confirmation', 'delivery-note', 'workshop-order'];
const previewPrefix: Record<OrderDocumentKind, string> = { offer: 'AN', confirmation: 'AB', 'delivery-note': 'LS', 'workshop-order': 'WA' };

/**
 * Beleg-Layout-Editor + Live-Vorschau in einem: eine Belegart-Auswahl steuert beides. Links ordnet der
 * Mandant die Blöcke (Reihenfolge per ▲▼, Sichtbarkeit per Schalter; Pflichtblöcke bleiben sichtbar),
 * rechts sieht er das Ergebnis sofort am Beispiel-Vorgang mit dem AKTUELLEN Profilentwurf. Das Layout
 * wandert als documentDefaults.blockLayouts ins Profil (Whole-Blob-jsonb, keine Migration).
 */
function DocumentLayoutSection({ profile, canEdit, onLayoutChange }: {
  readonly profile: TenantCompanyProfile;
  readonly canEdit: boolean;
  readonly onLayoutChange: (blockLayouts: Record<string, ReadonlyArray<DocumentBlockPlacement>>) => void;
}) {
  const [kind, setKind] = useState<OrderDocumentKind>('offer');
  const [lang, setLang] = useState<'de' | 'en'>('de');
  const layouts = profile.documentDefaults.blockLayouts;
  const blocks = useMemo(() => resolveOrderBlocks(kind, layouts), [kind, layouts]);
  const html = useMemo(() => renderOrderDocumentHtml({
    kind,
    documentNumber: `${previewPrefix[kind]}-2026-00042`,
    order: { ...previewSampleOrder, conditions: { ...previewSampleOrder.conditions, language: lang } },
    company: profile,
    issuedIso: '2026-08-01',
    machine: sampleTemplateMachine,
  }), [kind, lang, profile]);

  const writeBlocks = useCallback((next: readonly OrderBlockState[]) => {
    onLayoutChange({ ...layouts, [kind]: next.map((block) => ({ id: block.id, hidden: block.hidden })) });
  }, [layouts, kind, onLayoutChange]);
  const move = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    writeBlocks(next);
  }, [blocks, writeBlocks]);
  const toggleHidden = useCallback((index: number) => {
    writeBlocks(blocks.map((block, position) => (position === index ? { ...block, hidden: !block.hidden } : block)));
  }, [blocks, writeBlocks]);
  const resetLayout = useCallback(() => {
    const rest = { ...layouts };
    delete rest[kind];
    onLayoutChange(rest);
  }, [layouts, kind, onLayoutChange]);
  const isCustomized = (layouts[kind]?.length ?? 0) > 0;

  return (
    <div className="admin-company__layout is-wide">
      <div className="admin-company__preview-head">
        <span>Beleg-Layout &amp; Vorschau</span>
        <div className="admin-company__preview-tabs">
          {previewKinds.map((previewKind) => (
            <button key={previewKind} type="button" className={previewKind === kind ? 'is-active' : ''} onClick={() => setKind(previewKind)}>
              {orderDocumentLabels[previewKind]}
            </button>
          ))}
          <span className="admin-company__preview-sep" />
          <button type="button" className={lang === 'de' ? 'is-active' : ''} onClick={() => setLang('de')}>DE</button>
          <button type="button" className={lang === 'en' ? 'is-active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </div>
      <div className="admin-company__layout-body">
        <div className="admin-company__blocks">
          <p className="admin-company__note">Reihenfolge und Sichtbarkeit der Blöcke für <strong>{orderDocumentLabels[kind]}</strong>. Pflichtblöcke bleiben sichtbar.</p>
          <ul className="admin-company__block-list">
            {blocks.map((block, index) => (
              <li key={block.id} className={block.hidden ? 'is-hidden' : ''}>
                <span className="admin-company__block-order">
                  <button type="button" disabled={!canEdit || index === 0} onClick={() => move(index, -1)} aria-label="Nach oben"><ChevronUp size={14} /></button>
                  <button type="button" disabled={!canEdit || index === blocks.length - 1} onClick={() => move(index, 1)} aria-label="Nach unten"><ChevronDown size={14} /></button>
                </span>
                <span className="admin-company__block-label">{block.label}</span>
                {block.locked ? (
                  <span className="admin-company__block-lock"><Lock size={12} /> Pflicht</span>
                ) : (
                  <button type="button" className="admin-company__block-toggle" disabled={!canEdit} onClick={() => toggleHidden(index)}>
                    {block.hidden ? <><EyeOff size={13} /> Ausgeblendet</> : <><Eye size={13} /> Sichtbar</>}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {isCustomized && (
            <button type="button" className="admin-company__ghost" disabled={!canEdit} onClick={resetLayout}>
              <RotateCcw size={13} /> Standard-Layout wiederherstellen
            </button>
          )}
        </div>
        <iframe className="admin-company__preview-frame" title="Beleg-Vorschau" srcDoc={html} />
      </div>
    </div>
  );
}

function LetterheadPreview({ profile, tenantName }: { readonly profile: TenantCompanyProfile; readonly tenantName: string }) {
  const { letterhead, identity, contact } = profile;
  return (
    <figure className="admin-letterhead">
      <figcaption>
        <span>Vorschau · DIN A4</span>
        <button type="button" className="admin-company__ghost" onClick={() => window.print()}>
          <Printer size={13} /> Testdruck
        </button>
      </figcaption>
      <div
        className="admin-letterhead__page"
        style={{
          paddingTop: `${letterhead.marginTopMm}mm`,
          paddingLeft: `${letterhead.marginLeftMm}mm`,
          paddingRight: `${letterhead.marginRightMm}mm`,
          paddingBottom: `${letterhead.marginBottomMm}mm`,
          ['--letterhead-accent' as string]: letterhead.accentColor,
        }}
      >
        {letterhead.showFoldMarks && (
          <>
            <span className="admin-letterhead__mark is-fold1" aria-hidden />
            <span className="admin-letterhead__mark is-fold2" aria-hidden />
            <span className="admin-letterhead__mark is-punch" aria-hidden />
          </>
        )}

        <header className="admin-letterhead__head">
          <div className="admin-letterhead__slot is-left">
            {letterhead.logoDataUrl
              ? <img src={letterhead.logoDataUrl} alt="" />
              : <span className="admin-letterhead__logo--empty">{identity.name || tenantName}</span>}
          </div>
          <div className="admin-letterhead__slot is-center">{letterhead.logoCenterDataUrl && <img src={letterhead.logoCenterDataUrl} alt="" />}</div>
          <div className="admin-letterhead__slot is-right">{letterhead.logoRightDataUrl && <img src={letterhead.logoRightDataUrl} alt="" />}</div>
        </header>

        <div className="admin-letterhead__address">
          <small>{letterhead.senderLine || '— Absenderzeile nicht gesetzt —'}</small>
          <p>Mustermann GmbH<br />Herrn Max Mustermann<br />Musterstraße 12<br />12345 Musterstadt</p>
        </div>

        <div className="admin-letterhead__meta">
          <span>{contact.phone && `Tel. ${contact.phone}`}</span>
          <span>{contact.email}</span>
          <span>17.07.2026</span>
        </div>

        <div className="admin-letterhead__body">
          <strong>Angebot ANG-2026-00042</strong>
          <p>Sehr geehrter Herr Mustermann,</p>
          <p>so sieht Ihr Briefbogen aus. Kopf, Anschriftfeld, Ränder und Fußzeile kommen aus dieser Konfiguration – Angebote, Lieferscheine und Rechnungen benutzen später genau dieses Gerüst.</p>
        </div>

        <footer className="admin-letterhead__footer">
          {letterhead.footerColumns.map((column, index) => (
            <div key={index}>{column
              ? column.split('\n').map((row, rowIndex) => <span key={rowIndex}>{row}</span>)
              : <span className="is-empty">Spalte {index + 1}</span>}
            </div>
          ))}
        </footer>
      </div>
    </figure>
  );
}
